/**
 * Botão "Revisar Proposta"
 * Permite ao consultor reabrir a proposta para edição
 * e regenerar os itens do pedido com merge seguro (preserva operacionais)
 * 
 * Visível apenas se:
 * - Pedido tem lead_origem_id (veio de proposta)
 * - Pedido está no quadro COMERCIAL
 * 
 * Ao clicar:
 * - Muda status_lead para REVISANDO
 * - Salva status anterior para rollback seguro
 * - Registra pedido em revisão e timestamp
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { invalidarCacheConversor } from '@/hooks/useConverterPropostaEmItens';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { prepararPropostaRevisaoIA } from '@/lib/prepararPropostaRevisaoIA';

interface BotaoRevisarPropostaProps {
  pedido: { 
    id: string; 
    lead_origem_id: string | null;
    quadro_atual?: string;
  };
  isCancelado?: boolean;
  onRevisaoIniciada?: (pedidoId?: string) => void;
  /** Renderiza como botão-ícone compacto (para a coluna AÇÕES da lista de Cancelados) */
  compact?: boolean;
  /** Classe extra no botão não-compacto (ex.: cor) */
  className?: string;
}

export function BotaoRevisarProposta({ pedido, isCancelado = false, onRevisaoIniciada, compact = false, className }: BotaoRevisarPropostaProps) {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  // Só mostrar se:
  // 1. Tem lead de origem
  // 2. Está em COMERCIAL, PEDIDO_CANCELADO, ou sem quadro (pedido criado sem itens — bug de conversão)
  const podeRevisar = 
    pedido.lead_origem_id && 
    (!pedido.quadro_atual || pedido.quadro_atual === 'COMERCIAL' || pedido.quadro_atual === 'PEDIDO_CANCELADO');

  if (!podeRevisar) return null;

  const handleRevisar = async () => {
    if (!pedido.lead_origem_id) return;
    
    setLoading(true);
    try {
      // Limpar cache do conversor para garantir que PORTABILIDADE_PJ/PF seja rebuscado do banco
      invalidarCacheConversor();

      await prepararPropostaRevisaoIA(pedido.id, pedido.lead_origem_id);

      // Chamada única via RPC SECURITY DEFINER — resolve permissões de supervisor/consultor
      const { data, error } = await supabase
        .rpc('api_iniciar_revisao_proposta', {
          p_lead_id: pedido.lead_origem_id,
          p_pedido_id: pedido.id,
          p_is_cancelado: isCancelado,
        });

      if (error) throw error;

      const result = data as { ok: boolean; erro?: string; ja_revisando?: boolean; pedido_id?: string };

      if (!result.ok) {
        toast.error('Não foi possível revisar', { description: result.erro });
        setLoading(false);
        return;
      }

      if (result.ja_revisando) {
        toast.info('Revisão em andamento', {
          description: 'Continuando a revisão da proposta...',
        });
        onRevisaoIniciada?.(result.pedido_id);
        setLoading(false);
        return;
      }

      toast.success(isCancelado ? 'Pedido cancelado aberto para revisão!' : 'Proposta aberta para revisão!', {
        description: isCancelado
          ? 'Corrija os produtos e salve. O pedido irá para "PEDIDO RECUPERADO" ao concluir.'
          : 'Ao salvar, os itens serão regenerados. Para cancelar, clique em "Voltar para o Pedido".',
      });

      // Invalidar queries para refletir mudança
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead-detail', pedido.lead_origem_id] });
      queryClient.invalidateQueries({ queryKey: ['lead-completo', pedido.lead_origem_id] });
      queryClient.invalidateQueries({ queryKey: ['meus-leads-carteira-optimized'] });
      
      onRevisaoIniciada?.(result.pedido_id);
      
    } catch (error: any) {
      console.error('Erro ao revisar proposta:', error);
      const detalhe = error?.message
        || error?.details
        || error?.hint
        || (typeof error === 'string' ? error : JSON.stringify(error));
      toast.error('Erro ao abrir proposta', {
        description: detalhe || 'Falha sem detalhe retornado pelo servidor. Tente novamente e verifique sua conexão.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertDialogTrigger asChild>
              {compact ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-primary hover:text-primary hover:bg-primary/10"
                  disabled={loading}
                  onClick={(e) => e.stopPropagation()}
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              ) : (
                <Button variant="outline" size="sm" disabled={loading} className={className}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Revisar Proposta
                </Button>
              )}
            </AlertDialogTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isCancelado ? 'Revisar Proposta — recupera o pedido cancelado (ajustar e reenviar)' : 'Abre a proposta para edição e regenera os itens do pedido atual'}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-primary" />
            {isCancelado ? 'Revisar Pedido Cancelado' : 'Revisar Proposta'}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            {isCancelado ? (
              <>
                <p>Este pedido está <strong>cancelado</strong>. Você corrige a proposta e o pedido volta para <strong>"PEDIDO RECUPERADO"</strong> — <strong>nada é enviado ao cliente</strong>.</p>
                <p className="font-medium mt-2">Como funciona a recuperação:</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  <li><strong>Editar o plano</strong> de um item (mesmo tipo, ex.: 12GB → 40GB) atualiza o <strong>mesmo item</strong> no lugar (re-preço). O item <strong>nunca é apagado</strong>.</li>
                  <li><strong>Trocar o tipo</strong> (ex.: Fibra → TT, PN → HA) cria um <strong>item novo</strong>; o cancelado fica no histórico.</li>
                  <li>Adicionar uma linha nova vira <strong>item novo</strong>.</li>
                  <li>Linha que o cliente <strong>não quer mais</strong>: marque e clique em <strong>Deixar canceladas</strong>. Ela fica no cancelado e todo o resto volta.</li>
                  <li>Itens em andamento, finalizados ou de outros pedidos ficam <strong>bloqueados</strong> (não dá para mexer aqui).</li>
                </ul>
                <p className="text-primary font-medium mt-3 text-sm">
                   Corrija os produtos e clique em "Recuperar Pedido"para concluir.
                </p>
              </>
            ) : (
              <>
                <p>Isso vai:</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  <li>Abrir a proposta para edição no lead de origem</li>
                  <li>Marcar o lead como <strong>"Revisando"</strong></li>
                  <li>Ao salvar, os itens serão <strong>regenerados</strong></li>
                  <li>Dados operacionais (DDD, linha, gestor) serão <strong>preservados</strong></li>
                </ul>
                <p className="text-primary font-medium mt-3 text-sm">
                   Se você cancelar a revisão, o lead volta ao status anterior automaticamente.
                </p>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleRevisar}>
            {isCancelado ? 'Sim, revisar pedido cancelado' : 'Sim, revisar proposta'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
